// src/components/Ownerdashboard/ownerMedia.js
// Adding photos from an owner card is a two step flow: push the binary to the
// upload endpoint, then write the returned URLs back onto the property.
import apiClient from "../../common/apiClient";

const UPLOAD_URL = "/cjidnvij/ceksfbuebijn/user/uploadImages";
const EDIT_PROPERTY_URL =
  "/ijscui/probroker-admin/ceknvuhbd/cjwbhusb/protected/cedbvhu/edit/property/fcojkehfui";

// Both endpoints run on the staff/admin session, which an owner never has.
// The token below is a short-lived development one (it expires 24h after it was
// issued) - set REACT_APP_OWNER_MEDIA_TOKEN to swap it without a code change.
const AUTH_TOKEN =
  process.env.REACT_APP_OWNER_MEDIA_TOKEN ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2OGIyY2RlOTUxYzlkMGZlYTNhMTA2MzUiLCJpYXQiOjE3ODUxMzUxNTcsImV4cCI6MTc4NTIyMTU1N30.MRNXm3qjATme0L-xpE0T5uTu8ajo3WUQDYn0I5QXi8o";

const authHeader = { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } };

// A property carries at most 10 photos and each one has to stay under 10MB.
export const MAX_MEDIA_ITEMS = 10;
export const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Splits a freshly picked list into the files that may be uploaded and the ones
 * that were rejected, so the caller can show one message per reason.
 * `slots` is how many photos the property still has room for.
 */
export const screenFiles = (files, slots) => {
  const accepted = [];
  const tooLarge = [];
  const wrongType = [];
  let overflow = 0;

  files.forEach((file) => {
    if (!String(file.type || "").startsWith("image/")) {
      wrongType.push(file.name);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      tooLarge.push(file.name);
      return;
    }

    if (accepted.length >= slots) {
      overflow += 1;
      return;
    }

    accepted.push(file);
  });

  return { accepted, tooLarge, wrongType, overflow };
};

const pickMessage = (response, fallback) => {
  if (typeof response?.error === "string") return response.error;
  return response?.error?.message || response?.data?.message || fallback;
};

// The endpoint answers with { success, message, files: [{ originalName, url,
// type }] }, but older versions spelled it differently, so every known shape is
// accepted - and anything unrecognised falls back to a deep scan for http URLs.
const extractUrls = (body) => {
  const seen = [];

  const walk = (value) => {
    if (!value) return;

    if (typeof value === "string") {
      if (value.startsWith("http")) seen.push(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }

    if (typeof value === "object") {
      [
        value.url,
        value.imageUrl,
        value.imageURL,
        value.location,
        value.Location,
        value.files,
        value.file,
        value.urls,
        value.images,
        value.imageUrls,
        value.data,
      ].forEach(walk);
    }
  };

  walk(body);

  if (seen.length === 0) {
    const scan = (value) => {
      if (typeof value === "string") {
        if (value.startsWith("http")) seen.push(value);
        return;
      }
      if (Array.isArray(value)) {
        value.forEach(scan);
        return;
      }
      if (value && typeof value === "object") Object.values(value).forEach(scan);
    };

    scan(body);
  }

  return [...new Set(seen)];
};

/**
 * POST /cjidnvij/ceksfbuebijn/user/uploadImages
 * One request per file, each sent as multipart binary under `images`.
 * `onProgress(current, total)` fires as each file starts uploading.
 * Returns { success, urls }.
 */
export const uploadPropertyImages = async (files, onProgress) => {
  const urls = [];

  for (const [index, file] of files.entries()) {
    onProgress?.(index + 1, files.length);

    const form = new FormData();
    form.append("images", file);

    const response = await apiClient("post", UPLOAD_URL, form, authHeader);

    if (!response?.success) {
      return {
        success: false,
        urls,
        message: pickMessage(response, `Failed to upload ${file.name}.`),
      };
    }

    const uploaded = extractUrls(response.data);

    if (uploaded.length === 0) {
      return {
        success: false,
        urls,
        message: `Upload succeeded but no image URL came back for ${file.name}.`,
      };
    }

    urls.push(...uploaded);
  }

  return { success: true, urls };
};

/**
 * POST .../edit/property/fcojkehfui with { id, images }.
 * `images` is the full list the property should end up with - sent as an
 * array of individual URLs, one entry per image, never one joined string.
 */
export const savePropertyImages = async ({ id, images }) => {
  if (!id) {
    return { success: false, message: "This property is missing its id." };
  }

  // Accepts an array or a legacy comma blob; always posts a clean array.
  const imageList = Array.isArray(images)
    ? images.filter(Boolean)
    : flattenImages(images);

  const response = await apiClient(
    "post",
    EDIT_PROPERTY_URL,
    { id, images: imageList },
    authHeader
  );

  if (!response?.success) {
    return {
      success: false,
      message: pickMessage(response, "Failed to attach the images."),
    };
  }

  return { success: true, data: response.data };
};

// property.images can hold a comma separated blob in a single entry, so the
// existing photos are flattened before the new ones are appended.
export const flattenImages = (images) => {
  if (!Array.isArray(images)) {
    return typeof images === "string" ? images.split(",").map((url) => url.trim()).filter(Boolean) : [];
  }

  return images
    .flatMap((entry) => (typeof entry === "string" ? entry.split(",") : []))
    .map((url) => url.trim())
    .filter(Boolean);
};
